import React from "react";
import FlymyaIconLoading from "./FlymyaIconLoading";
import FlightSearchLoadingModal from "./flight/FlightSearchLoadingModal";
import { formatResultDisplayDate } from "../utils/date";
import {
  CABIN_CLASS_FILTER,
  FLIGHT_SKIP_SEARCH_MODAL_KEY,
} from "../config/constants";

/**
 * Whether the results page this fallback is covering was entered from the
 * customer details page ("Change Fare Class", or the mobile back header). Only
 * peeked at: the results page itself consumes the marker once it mounts, and
 * clearing it here would hand that page back its own search modal.
 */
const isReturningFromCustomerDetails = () => {
  try {
    return sessionStorage.getItem(FLIGHT_SKIP_SEARCH_MODAL_KEY) === "1";
  } catch (err) {
    return false;
  }
};

/**
 * The flight results URL, whatever prefixes it carries:
 *
 *   /:locale[/in-app|/in-web]/flight/:flightType[/roundtrip]
 *     /:from-:to/:departureDate/:returnDate/:nationality
 *     /:b2bClassType/:adult-:child-:infant[/:departureUUID[/:returnUUID]]
 *
 * Read relative to the "flight" segment rather than from the front, so the
 * in-app and in-web prefixes need no special casing. The route pair and the
 * DD-MM-YYYY departure date are what separate a results URL from the other
 * /flight/ pages (customer-details, payment, ...).
 */
export const parseFlightResultPath = (pathname) => {
  const segments = (pathname || "").split("/").filter(Boolean);
  const flightIndex = segments.indexOf("flight");
  if (flightIndex === -1) return null;

  // Drop "flight" and :flightType, then the optional inter-roundtrip marker.
  let rest = segments.slice(flightIndex + 2);
  const isInterRoundtrip = rest[0] === "roundtrip";
  if (isInterRoundtrip) rest = rest.slice(1);

  const [route, departureDate, returnDate, , , passengers] = rest;

  if (!route?.includes("-")) return null;
  if (!/^\d{2}-\d{2}-\d{4}$/.test(departureDate || "")) return null;

  const [from, to] = route.split("-");
  if (!from || !to) return null;

  const counts = (passengers || "").split("-");
  const passengerCount =
    counts.slice(0, 3).reduce((sum, part) => sum + (Number(part) || 0), 0) || 1;

  const hasReturn = Boolean(returnDate) && returnDate !== "0";

  return {
    // Only the IATA code is in the URL - the city names the results header
    // shows come from a query that has not run yet at this point.
    fromLabel: from.toUpperCase(),
    toLabel: to.toUpperCase(),
    isRoundTrip: isInterRoundtrip || hasReturn,
    dateLabel: `${formatResultDisplayDate(departureDate)}${
      hasReturn ? ` - ${formatResultDisplayDate(returnDate)}` : ""
    }`,
    passengerCount,
  };
};

/**
 * The app-wide loader.
 *
 * Searching from the home page leaves via `window.location.href`, so the
 * results page arrives as a fresh boot rather than a route transition - the
 * theme request and the FlightResult chunk are both still outstanding, and
 * this is what covers that gap. On a results URL it shows the same search
 * card FlightResult itself holds open while operators report in, so the
 * traveller sees one continuous modal instead of the full-screen Flymya
 * animation swapping to a card. Everywhere else - and on the way back from the
 * customer details page, where no new search is being asked for - the
 * full-screen loader stands.
 */
const AppRouteLoading = () => {
  const search = parseFlightResultPath(window.location.pathname);

  // A return to a search already seen: the chunk still has to load, but the
  // search card would claim a search is running for a trip the traveller only
  // wanted to reprice. The plain loader carries the gap instead.
  if (!search || isReturningFromCustomerDetails()) return <FlymyaIconLoading />;

  const cabinClass = localStorage.getItem(CABIN_CLASS_FILTER);

  return (
    <FlightSearchLoadingModal
      open
      fromLabel={search.fromLabel}
      toLabel={search.toLabel}
      isRoundTrip={search.isRoundTrip}
      dateLabel={search.dateLabel}
      passengerCount={search.passengerCount}
      // i18n has not necessarily loaded this early, so these stay on the
      // English defaults the results page falls back to.
      tripTypeLabel={search.isRoundTrip ? "Round Trip" : "One Way Trip"}
      cabinClassLabel={
        cabinClass && cabinClass !== "All Cabin Classes"
          ? `${cabinClass} Class`
          : "All Cabin Classes"
      }
    />
  );
};

export default AppRouteLoading;
